import { Money } from '@/core/money';

/**
 * The one money formatter for live engagement surfaces.
 *
 * Denomination is NOT re-derived here: the api sends the currency alongside
 * every amount (`USD` for SMB accounts, `OFC` credits otherwise, per
 * `BillingClassification.tla`), and `core/money` already knows how to render
 * both. A null amount means the viewer isn't entitled to it (§6.3) and callers
 * must render nothing rather than pass 0 — the signature enforces that by
 * refusing null.
 */
export function formatEngagementMoney(cents: number, currency: string | null): string {
  return new Money(cents, currency ?? 'USD').formatted({ omitEmptyCents: true });
}

/** Hours arrive as decimal strings; render them without trailing `.00` noise. */
export function formatHours(hours: number): string {
  return `${Number.isInteger(hours) ? hours : hours.toFixed(2)}h`;
}

/**
 * The api's `Hours` scalar: a positive decimal with at most two places.
 *
 * Every hours field in the feature validates against this one pattern, so the
 * expert's submission and the approver's partial agree on what counts as a
 * number — `Number.parseFloat` alone does not, since it reads `'8abc'` as 8.
 */
export const HOURS_PATTERN = /^\d+(\.\d{1,2})?$/;

/** A dollar figure as people write it: plain or comma-grouped, two places at most. */
const DOLLARS_PATTERN = /^(\d+|\d{1,3}(,\d{3})+)(\.\d{1,2})?$/;

/**
 * A typed dollar amount as positive cents, or null when it isn't one.
 *
 * `Number.parseFloat` read `'50,000'` as 50 and `'1e5'` as 100,000, so a
 * governance adjustment could propose a ceiling nobody typed. A leading `$` and
 * well-formed thousands separators are accepted because that is how money is
 * written; anything else is refused rather than guessed at.
 *
 * The cents are assembled from the digits rather than computed as
 * `dollars * 100`, whose float product drifts by a cent on large figures, so
 * every amount up to `MAX_SAFE_INTEGER` cents is exact and a longer one is
 * refused rather than rounded.
 */
export function parseDollarsToCents(input: string): number | null {
  const typed = input.trim().replace(/^\$/, '');
  if (!DOLLARS_PATTERN.test(typed)) return null;
  const [whole, fraction = ''] = typed.replace(/,/g, '').split('.');
  const cents = Number(whole + fraction.padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

/**
 * The consumed-against-NTE cell shared by every live engagement list.
 *
 * A null `nteHoursTotal` means no offer has priced the engagement yet, which is
 * a real state rather than zero — so name the state instead of rendering
 * "0h of 0h" or a bare dash, either of which reads as missing data. Lifted here
 * so the dashboard and the project section cannot drift apart on that copy.
 */
export function formatNteHours(nteHoursConsumed: number, nteHoursTotal: number | null): string {
  if (nteHoursTotal === null) return 'Not yet priced';
  return `${formatHours(nteHoursConsumed)} of ${formatHours(nteHoursTotal)}`;
}
